import { PrincipalFormData, PrincipalFormErrors } from "./types";

export const validatePrincipalForm = (
  formData: PrincipalFormData,
  isEditMode: boolean = false
): { hasError: boolean; errors: PrincipalFormErrors } => {
  const errors: PrincipalFormErrors = {
    name: "",
    principalCode: "",
    shortName: "",
    merchantId: "",
    logoUrl: "",
    contractDocumentUrl: "",
    commissionPercentage: "",
    commissionFixedAmount: "",
    voucherType: "",
    country: "",
    commissionType: "",
    paymentTerm: "",
    principalConversionRate: "",
    codeActivationFeeAmount: "",
    codeActivationFeeCurrency: "",
    principalBanks: "",
    contactInformation: {
      email: "",
      phone: "",
      contactPerson: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
      },
    },
    whiteListedIps: "",
    email: "",
    password: "",
    commissionSlabs: "",
  };

  let hasError = false;

  if (!formData.name || formData.name.trim() === "") {
    hasError = true;
    errors.name = "Please enter a valid principal name";
  }

  if (!formData.principalCode || formData.principalCode.trim() === "") {
    hasError = true;
    errors.principalCode = "Please enter a valid principal code";
  }

  if (!formData.shortName || formData.shortName.trim() === "") {
    hasError = true;
    errors.shortName = "Please enter a valid short name";
  }

  // Validate commission based on type
  if (formData.commissionType === "percentage" || formData.commissionType === "precentage") {
    if (!formData.commissionPercentage || isNaN(parseFloat(formData.commissionPercentage))) {
      hasError = true;
      errors.commissionPercentage = "Please enter a valid commission percentage";
    }
  } else if (formData.commissionType === "fixed") {
    if (!formData.commissionFixedAmount || isNaN(parseFloat(formData.commissionFixedAmount))) {
      hasError = true;
      errors.commissionFixedAmount = "Please enter a valid commission fixed amount";
    }
  }

  if (!formData.commissionType) {
    hasError = true;
    errors.commissionType = "Please select a commission type";
  }

  // Voucher type is optional (can be null for distribution-only principals)
  // Only validate if not distribution only
  if (!formData.isDistributionOnly && !formData.voucherType) {
    hasError = true;
    errors.voucherType = "Please select a voucher type";
  }

  if (!formData.country) {
    hasError = true;
    errors.country = "Please select a country";
  }

  // Email is required only in create mode (field is not editable/visible in edit)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!isEditMode) {
    if (!formData.email || !emailRegex.test(formData.email)) {
      hasError = true;
      errors.email = "Please enter a valid email";
    }
  }

  // Password validation - required for create, optional for edit
  if (!isEditMode && (!formData.password || formData.password.length < 6)) {
    hasError = true;
    errors.password = "Please enter a valid password (minimum 6 characters)";
  }

  if (formData.addSelfDistributor && !formData.distributorId) {
    hasError = true;
    errors.distributorId = "Please select a distributor";
  }

  return { hasError, errors };
};

export const scrollToFirstError = (errors: PrincipalFormErrors) => {
  const errorFields = [
    { key: 'name', id: 'name' },
    { key: 'principalCode', id: 'principalCode' },
    { key: 'shortName', id: 'shortName' },
    { key: 'merchantId', id: 'merchantId' },
    { key: 'commissionPercentage', id: 'commissionPercentage' },
    { key: 'commissionFixedAmount', id: 'commissionFixedAmount' },
    { key: 'voucherType', id: 'voucherType' },
    { key: 'country', id: 'country' },
    { key: 'commissionType', id: 'commissionType' },
    { key: 'email', id: 'email' },
    { key: 'password', id: 'password' },
    { key: 'distributorId', id: 'distributorId' },
  ];

  for (const field of errorFields) {
    if (errors[field.key as keyof PrincipalFormErrors]) {
      const errorElement = document.getElementById(field.id);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
        break;
      }
    }
  }
};
